"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { useTaskContext } from "@/contexts/TaskContext";
import { useBotworkContext } from "@/contexts/BotworkContext";
import DesktopSearchModal from "./DesktopSearchModal";

// Desktop sidebar. Single-component render with a width-transitioning
// glass panel plus a fixed-position toolbar that always stays mounted
// so the collapse toggle never unmounts.

const F = "'Plus Jakarta Sans', sans-serif";

// Figma 91:10009 / 91:9547 — literal positions.
// Aside is at window (8, 8) with px-16 py-16.24; header row has
// pb-16 and gap-16; logo slot 60×14 then collapse 14×14 then
// search 14×14. So in window coords the header icons live at:
//   collapse: (100, 24.24)  ← logo slot ends at x:84, +16 gap
//   search:   (130, 24.24)  ← +14 icon +16 gap
// First nav item starts at window y:54.24 (= 24.24 + 14 + 16).
const TOOLBAR_TOP = 24;
const TOOLBAR_LEFT = 100;
const SIDEBAR_CONTENT_TOP = 46;

export default function DesktopSidebar() {
  const ctx = useTaskContext();
  const botwork = useBotworkContext();
  const router = useRouter();
  const pathname = usePathname();

  const [collapsed, setCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  // Drive the CSS attribute that swaps `.app-main-area` padding-left.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.sidebarCollapsed = collapsed ? "true" : "false";
  }, [collapsed]);

  const onNewTask = () => {
    ctx.dispatch({ type: "RESET" });
    if (pathname !== "/app") router.push("/app");
  };
  const onSignIn = () => ctx.openLogin();
  const onSelect = (_id: string) => {
    ctx.dispatch({ type: "RESET" });
    if (pathname !== "/app") router.push("/app");
  };
  const onOpenMarketplace = () => {
    // Marketplace is a real page now (/app/marketplace) — navigate to
    // it like Network/Training/Assets. Previously this button popped
    // a modal overlay on the chat page; that path is gone.
    router.push("/app/marketplace");
  };

  const signedIn = ctx.user.isSignedIn;
  const balance = botwork.mode === "live" ? botwork.balance : ctx.user.balance;

  const seen = new Set<string>();
  const recents = ctx.user.taskHistory
    .filter((h) => {
      const k = h.title.trim().toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .map((h) => ({ id: h.id, title: h.title, agentName: h.agentName }));

  const searchPortal =
    searchOpen && typeof document !== "undefined"
      ? createPortal(
          <DesktopSearchModal onClose={() => setSearchOpen(false)} />,
          document.body,
        )
      : null;

  return (
    <>
      {/* Glass sidebar panel — width animates between 0 and 260. */}
      <aside
        className={collapsed ? undefined : "desktop-glass-panel"}
        style={{
          position: "fixed",
          top: 8,
          left: 8,
          width: collapsed ? 0 : 260,
          height: "calc(100vh - 16px)",
          borderRadius: 8,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 10,
          transition: "width 250ms ease-out",
        }}
      >
        {/* Fixed inner width so text doesn't reflow during the
         * collapse animation — the aside's overflow:hidden clips. */}
        <div
          style={{
            width: 260,
            display: "flex",
            flexDirection: "column",
            flex: "1 0 0",
            minHeight: 0,
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: SIDEBAR_CONTENT_TOP,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
              flex: "1 0 0",
              minHeight: 0,
              overflow: "clip",
            }}
          >
            <nav
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                // Shift the four nav buttons 6 px left of the standard
                // px-16 content gutter. Recents + user-card keep the
                // original alignment.
                marginLeft: -6,
                width: "calc(100% + 6px)",
              }}
            >
              <DesktopNavButton icon="plus-circle" label="New task" filled onClick={onNewTask} />
              <div style={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
                <DesktopNavButton icon="shop" label="Marketplace" onClick={onOpenMarketplace} />
                <DesktopNavButton icon="bookmark" label="Bookmarked" href="/app/bookmarks" />
                <DesktopNavButton icon="folder-open" label="Assets" href="/app/assets" />
                <DesktopNavButton icon="location-arrow" label="Network" href="/app/network" />
                <DesktopNavButton icon="star-large-outline" label="Training" href="/app/training" />
              </div>
            </nav>

            {recents.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                {recents.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => onSelect(conv.id)}
                    className="app-recent-item"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      paddingBottom: 2,
                      width: "100%",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <p
                      style={{
                        fontFamily: F,
                        fontWeight: 500,
                        fontSize: 12,
                        lineHeight: "20px",
                        color: "#E5E2E1",
                        margin: 0,
                        marginBottom: -2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                    >
                      {conv.title}
                    </p>
                    <p
                      style={{
                        fontFamily: F,
                        fontWeight: 400,
                        fontSize: 11,
                        lineHeight: "16.5px",
                        color: "#CDC2D8",
                        margin: 0,
                        marginBottom: -2,
                      }}
                    >
                      {conv.agentName}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {signedIn ? (
          <div style={{ width: 260 }}>
            <DesktopUserCard
              name={ctx.user.name}
              initials={ctx.user.initials}
              balance={balance}
            />
          </div>
        ) : (
          <div style={{ width: 260 }}>
            <DesktopSignIn onClick={onSignIn} />
          </div>
        )}
      </aside>

      {/* Persistent toolbar — always mounted so the toggle button
       * can never unmount. Sits at the window's top-left in window
       * coords; when the sidebar is expanded it visually overlaps
       * the sidebar's padding area, when collapsed it floats over
       * the main area. */}
      <div
        style={{
          position: "fixed",
          top: TOOLBAR_TOP,
          left: TOOLBAR_LEFT,
          display: "flex",
          alignItems: "center",
          gap: 16,
          zIndex: 11,
        }}
      >
        <button
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 14,
            height: 14,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <img src="/app-icons/web-section-alt.svg" alt="" style={{ width: 14, height: 14 }} />
        </button>
        <button
          onClick={() => setSearchOpen(true)}
          aria-label="Search"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 14,
            height: 14,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <img src="/app-icons/search.svg" alt="" style={{ width: 14, height: 14 }} />
        </button>
      </div>

      {searchPortal}
    </>
  );
}

function DesktopNavButton({
  icon,
  label,
  filled,
  onClick,
  href,
}: {
  icon: string;
  label: string;
  filled?: boolean;
  onClick?: () => void;
  href?: string;
}) {
  const inner = (
    <div
      className={filled ? "app-nav-btn-new" : "app-nav-btn"}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: filled ? "space-between" : "flex-start",
        gap: filled ? 0 : 8,
        // Figma 91:10123: filled "New task" uses px-12 py-8; other
        // transparent nav buttons stay at p-8.
        padding: filled ? "8px 12px" : 8,
        borderRadius: 9999,
        background: filled ? "#2D2C2C" : "transparent",
        width: "100%",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16.842,
          height: 16.842,
          flexShrink: 0,
        }}
      >
        <span style={{ display: "flex", transform: "scale(1.05)" }}>
          <img src={`/app-icons/${icon}.svg`} alt="" style={{ width: 16, height: 16 }} />
        </span>
      </span>
      <span
        style={{
          fontFamily: F,
          fontWeight: 600,
          fontSize: 12,
          lineHeight: "21px",
          color: "#F6F5F5",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      {filled && <span style={{ width: 21.053, height: 21.053, flexShrink: 0 }} />}
    </div>
  );

  const wrapper = (
    <div
      style={{
        display: "flex",
        height: filled ? 35.2 : 35.15,
        alignItems: "center",
        width: "100%",
      }}
    >
      <div style={{ transform: "scale(0.95)", width: "100%" }}>{inner}</div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} style={{ textDecoration: "none", display: "block", width: "100%" }}>
        {wrapper}
      </Link>
    );
  }
  return (
    <div onClick={onClick} style={{ cursor: onClick ? "pointer" : "default", width: "100%" }}>
      {wrapper}
    </div>
  );
}

function DesktopUserCard({
  name,
  initials,
  balance,
}: {
  name: string;
  initials: string;
  balance: number;
}) {
  return (
    <div
      className="app-user-card"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 12,
        paddingBottom: 12,
        borderTop: "1px solid #2C2B2B",
      }}
      title={`${name} · Balance: $${balance.toFixed(2)}`}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 33.684,
          height: 33.684,
          flexShrink: 0,
        }}
      >
        <div style={{ transform: "scale(1.05)" }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 9999,
              background: "#8635F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontFamily: F,
                fontWeight: 600,
                fontSize: 11.2,
                lineHeight: "16.8px",
                color: "#F6F5F5",
              }}
            >
              {initials}
            </span>
          </div>
        </div>
      </div>
      <div style={{ flex: "1 0 0", minWidth: 0 }}>
        <div style={{ transform: "scale(1.05)", transformOrigin: "left center" }}>
          <p
            style={{
              fontFamily: F,
              fontWeight: 600,
              fontSize: 12,
              lineHeight: "21px",
              color: "#F6F5F5",
              margin: 0,
              marginBottom: -2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </p>
          <p
            style={{
              fontFamily: F,
              fontWeight: 400,
              fontSize: 11,
              lineHeight: "16.5px",
              color: "#CDC2D8",
              margin: 0,
              marginBottom: -2,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            Node Keeper
          </p>
        </div>
      </div>
    </div>
  );
}

function DesktopSignIn({ onClick }: { onClick: () => void }) {
  return (
    <div
      style={{
        borderTop: "1px solid #2C2B2B",
        padding: "12px 16px 20px",
      }}
    >
      <button
        onClick={onClick}
        className="app-signin-btn"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          width: "100%",
          background: "transparent",
          border: "2px solid #2E2A33",
          borderRadius: 9999,
          cursor: "pointer",
        }}
      >
        <img src="/app-icons/signin.svg" alt="" style={{ width: 16, height: 16 }} />
        <span
          style={{
            fontFamily: F,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: "21px",
            color: "#F6F5F5",
          }}
        >
          Sign in
        </span>
      </button>
    </div>
  );
}
